import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import useWmsPolling from '../../shared/useWmsPolling';
import countingService, { CountTaskDto, CountingStats, TodayProductResult } from '../../services/countingService';
import { locationService } from '../../services/locationService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsListSection from '../../shared/WmsListSection';
import WmsScannerPanel from '../../shared/WmsScannerPanel';

export interface QuickCountLocation {
  locationId: string;
  locationCode: string;
}

interface CountTaskSelectProps {
  personnelName: string;
  personnelId?: string;
  warehouseId: string;
  onTaskSelected: (task: CountTaskDto) => void;
  onQuickCount: (location: QuickCountLocation) => void;
}

const normalizeScan = (v: unknown) =>
  String(v ?? '').trim().toUpperCase().replace(/\s+/g, '');

const CountTaskSelect: React.FC<CountTaskSelectProps> = ({
  personnelName, personnelId, warehouseId, onTaskSelected, onQuickCount,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();

  const [todayOpen, setTodayOpen] = useState(false);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayProducts, setTodayProducts] = useState<TodayProductResult[]>([]);

  const handleBadgeClick = useCallback(async () => {
    setTodayOpen(true);
    setTodayLoading(true);
    try {
      const data = await countingService.getTodayProducts(warehouseId);
      setTodayProducts(data);
    } catch {
      notify({ type: 'error', message: 'Bugünkü sayım listesi yüklenirken hata oluştu.' });
    }
    setTodayLoading(false);
  }, [warehouseId, notify]);

  const { data: tasks, refresh } = useWmsPolling<CountTaskDto[]>(
    async () => {
      if (!personnelId) return [];
      return countingService.getMyTasksByPersonnel(warehouseId, personnelId);
    },
    5000,
    !!warehouseId && !!personnelId
  );

  const { data: stats } = useWmsPolling<CountingStats>(
    () => countingService.getStats(warehouseId),
    10000,
    !!warehouseId
  );

  const handleStart = async (task: CountTaskDto) => {
    try {
      if (task.status === 'Created') await countingService.startTask(task.id);
      onTaskSelected(task);
    } catch {
      notify({ type: 'error', message: t('screens.counting.taskSelect.startFailed') });
    }
  };

  // Hızlı sayım: okutulan barkodu gerçek lokasyona çöz, bulunamazsa uyar.
  const handleQuickScan = async (barcode: string) => {
    const target = normalizeScan(barcode);
    if (!target) return;
    try {
      const res: any = await locationService.getByWarehouse(warehouseId);
      const rows = res?.data?.data ?? res?.data?.Data ?? res?.data ?? [];
      const list: any[] = Array.isArray(rows) ? rows : [];
      const match = list.find(r =>
        normalizeScan(r?.Code ?? r?.code) === target
        || normalizeScan(r?.BarcodeData ?? r?.barcodeData) === target
        || normalizeScan(r?.Id ?? r?.id) === target
      );
      const locId = match?.Id ?? match?.id;
      if (!locId) {
        notify({ type: 'error', message: t('screens.counting.taskSelect.quickLocationNotFound', { code: barcode }) });
        return;
      }
      onQuickCount({ locationId: String(locId), locationCode: String(match?.Code ?? match?.code ?? barcode) });
    } catch {
      notify({ type: 'error', message: t('screens.counting.taskSelect.quickLocationNotFound', { code: barcode }) });
    }
  };

  const taskList = tasks || [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      <WmsNotification notification={notification} onClose={clear} />

      <WmsStationHeader
        title={t('screens.counting.taskSelect.title')}
        subtitle={t('screens.counting.taskSelect.subtitle', { name: personnelName })}
        badgeLabel={t('screens.counting.taskSelect.badge', { tasks: stats?.todayTasks ?? 0, locations: stats?.todayLocations ?? 0 })}
        badgeColor="info"
        badgeIcon={<AssignmentIcon />}
        onBadgeClick={handleBadgeClick}
      />

      <WmsListSection
        title={t('screens.counting.taskSelect.listTitle')}
        count={taskList.length}
        onRefresh={refresh}
        hasData={taskList.length > 0}
        emptyText={t('screens.counting.taskSelect.empty')}
      >
        <Paper sx={{ bgcolor: 'background.paper', overflow: 'auto' }}>
          <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.counting.taskSelect.colTaskNo')}</TableCell>
                  <TableCell>{t('screens.counting.taskSelect.colName')}</TableCell>
                  <TableCell>{t('screens.counting.taskSelect.colType')}</TableCell>
                  <TableCell align="center">{t('screens.counting.taskSelect.colLocation')}</TableCell>
                  <TableCell align="center">{t('screens.counting.taskSelect.colProgress')}</TableCell>
                  <TableCell align="center">{t('screens.counting.taskSelect.colStatus')}</TableCell>
                  <TableCell align="center">{t('screens.counting.taskSelect.colAction')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskList.map(task => {
                  const progress = task.totalLocations > 0 ? (task.countedLocations / task.totalLocations) * 100 : 0;
                  return (
                    <TableRow key={task.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{task.taskNumber}</TableCell>
                      <TableCell>{task.taskName}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            task.countType === 'Full' ? t('screens.counting.taskSelect.typeFull') :
                            task.countType === 'Zone' ? t('screens.counting.taskSelect.typeZone') :
                            task.countType === 'Random'
                              ? t('screens.counting.taskSelect.typeRandomWithCount', {
                                  count: task.randomCount ?? task.totalLocations,
                                })
                              :
                            task.countType === 'Single' ? t('screens.counting.taskSelect.typeSingle') : task.countType
                          }
                          size="small" variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">{task.totalLocations}</TableCell>
                      <TableCell align="center" sx={{ minWidth: 120 }}>
                        {task.status === 'InProgress' ? (
                          <Box>
                            <LinearProgress variant="determinate" value={progress}
                              sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                            <Typography variant="caption">
                              {task.countedLocations}/{task.totalLocations}
                            </Typography>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={task.status === 'Created' ? t('screens.counting.taskSelect.statusNew') : t('screens.counting.taskSelect.statusInProgress')}
                          size="small"
                          color={task.status === 'Created' ? 'default' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleStart(task)}
                        >
                          {task.status === 'Created' ? t('screens.counting.taskSelect.startBtn') : t('screens.counting.taskSelect.continueBtn')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
          </Table>
        </Paper>
      </WmsListSection>

      <WmsScannerPanel
        title={t('screens.counting.taskSelect.quickTitle')}
        placeholder={t('screens.counting.taskSelect.quickPlaceholder')}
        onScan={handleQuickScan}
        maxWidth={500}
      />

      {/* Bugünkü sayım detay dialog */}
      <Dialog open={todayOpen} onClose={() => setTodayOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="info" />
            <Typography variant="h6">
              Bugünkü Sayım — {stats?.todayTasks ?? 0} görev, {stats?.todayLocations ?? 0} lokasyon
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setTodayOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {todayLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography color="text.secondary" sx={{ mt: 2 }}>Yükleniyor...</Typography>
            </Box>
          ) : todayProducts.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Bugün henüz sayım yapılmamış.</Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Görev No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lokasyon</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ürün</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Sistem</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Sayılan</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Fark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Saat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {todayProducts.map(r => (
                  <TableRow key={r.resultId} hover
                    sx={{ bgcolor: r.difference !== 0 ? 'warning.50' : 'inherit' }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.taskNumber ?? '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.locationCode}</TableCell>
                    <TableCell>
                      {r.isEmpty ? (
                        <Chip label="Boş Lokasyon" size="small" variant="outlined" color="default" />
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {r.productName ?? r.barcode ?? r.stockCode ?? '-'}
                          </Typography>
                          {r.stockCode && (
                            <Typography variant="caption" color="text.secondary">{r.stockCode}</Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">{r.isEmpty ? '-' : r.systemQuantity}</TableCell>
                    <TableCell align="center">{r.isEmpty ? 0 : r.countedQuantity}</TableCell>
                    <TableCell align="center">
                      {r.isEmpty ? (
                        <Chip label="0" size="small" color="default" />
                      ) : (
                        <Chip
                          label={r.difference > 0 ? `+${r.difference}` : String(r.difference)}
                          size="small"
                          color={r.difference === 0 ? 'success' : 'warning'}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {r.countedAt ? new Date(r.countedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
            {todayProducts.length} kayıt
          </Typography>
          <Button onClick={() => setTodayOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CountTaskSelect;
